import React, { forwardRef, useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import enLocale from 'date-fns/locale/en-US'
import esLocale from 'date-fns/locale/es'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import Tooltip from '../Popups/Tooltip'
import useDatePickerBase from './DatePicker.hook'
import {
  BaseDatePicker,
  BaseInput,
  BottomMessage,
  CalendarIcon,
  DatePickerProvider,
  Error,
  Info,
  Label,
  Required,
  Root,
  Skeleton,
  Suffix,
  TooltipIcon,
  Wrapper
} from './DatePicker.styles'

export const useDatePicker = useDatePickerBase

const LOCALE_DICTIONARY = {
  en: enLocale,
  es: esLocale
}

/**
 * The Date Picker' component.
 */
const DatePicker = forwardRef((props, ref) => {
  const {
    dialogMargin,
    disabled,
    disableFuture,
    disablePast,
    disableToolbar,
    error,
    format,
    info,
    label,
    loading,
    locale,
    margin,
    maxDate,
    minDate,
    padding,
    placeholder,
    required,
    size,
    success,
    textAlign,
    tooltip,
    value,
    verticalAlign,
    width,
    onChange,
    ...restProps
  } = props
  const openRef = useRef(false)
  const [open, setOpen] = useState(false)
  const handleBlur = useCallback(
    event => {
      if (restProps?.onBlur && typeof restProps?.onBlur === 'function') {
        restProps?.onBlur(event)
      }

      if (openRef.current) {
        return
      }

      onChange(value)
    },
    [onChange, restProps, value]
  )
  const handleClick = useCallback(event => event.stopPropagation(), [])
  const handleClose = useCallback(() => {
    openRef.current = false
    setOpen(false)
  }, [])
  const handleOpen = useCallback(() => {
    openRef.current = true
    setOpen(true)
  }, [])
  const renderInput = useCallback(
    textFieldProps => (
      <BaseInput
        {...textFieldProps.InputProps}
        disabled={textFieldProps.disabled}
        endAdornment={
          <Suffix disabled={disabled} onClick={handleOpen}>
            <CalendarIcon size={size} />
          </Suffix>
        }
        error={Boolean(error)}
        inputProps={textFieldProps.inputProps}
        placeholder={placeholder}
        size={size}
        success={success}
        textAlign={textAlign}
        onBlur={handleBlur}
      />
    ),
    [disabled, error, handleBlur, handleOpen, placeholder, size, success, textAlign]
  )

  return (
    <DatePickerProvider adapterLocale={LOCALE_DICTIONARY[locale]} dateAdapter={AdapterDateFns}>
      <Root margin={margin} padding={padding} ref={ref} width={width}>
        {label && (
          <Label size={size}>
            {label}
            {required && <Required size={size} />}
            {tooltip && (
              <Tooltip content={tooltip}>
                <TooltipIcon focusable tanIndex="0" size={size} />
              </Tooltip>
            )}
          </Label>
        )}
        <Wrapper>
          {loading && <Skeleton size={size} variant="rectangular" />}
          {!loading && (
            <BaseDatePicker
              {...restProps}
              closeOnSelect
              dialogMargin={dialogMargin}
              disabled={disabled}
              disableFuture={disableFuture}
              disablePast={disablePast}
              disableToolbar={disableToolbar}
              inputFormat={format}
              maxDate={maxDate}
              minDate={minDate}
              open={open}
              renderInput={renderInput}
              toolbarTitle=""
              value={value}
              verticalAlign={verticalAlign}
              onChange={onChange}
              onClick={handleClick}
              onClose={handleClose}
              onOpen={handleOpen}
            />
          )}
        </Wrapper>
        <BottomMessage size={size}>
          {!error && <Info>{info}</Info>}
          {error && !loading && <Error>{error}</Error>}
        </BottomMessage>
      </Root>
    </DatePickerProvider>
  )
})

DatePicker.defaultProps = {
  dialogMargin: '100px auto',
  disabled: false,
  disableFuture: false,
  disablePast: false,
  disableToolbar: false,
  error: '',
  format: 'dd/MM/yyyy',
  info: '',
  label: '',
  loading: false,
  locale: 'es',
  margin: 0,
  maxDate: undefined,
  minDate: undefined,
  padding: 0,
  placeholder: '',
  required: false,
  size: 'medium',
  success: false,
  textAlign: 'initial',
  tooltip: '',
  value: null,
  verticalAlign: 'center',
  width: '100%'
}

DatePicker.propTypes = {
  /**
   * Dialog margin.
   */
  dialogMargin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Disable future.
   */
  disableFuture: PropTypes.bool,
  /**
   * Disable past.
   */
  disablePast: PropTypes.bool,
  /**
   * Disable toolbar.
   */
  disableToolbar: PropTypes.bool,
  /**
   * Error.
   */
  error: PropTypes.string,
  /**
   * Format.
   */
  format: PropTypes.string,
  /**
   * Info.
   */
  info: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /**
   * Label.
   */
  label: PropTypes.string,
  /**
   * Loading.
   */
  loading: PropTypes.bool,
  /**
   * Locale.
   */
  locale: PropTypes.oneOf(['en', 'es']),
  /**
   * Margin.
   */
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Max date.
   */
  maxDate: PropTypes.instanceOf(Date),
  /**
   * Min date.
   */
  minDate: PropTypes.instanceOf(Date),
  /**
   * Padding.
   */
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Placeholder.
   */
  placeholder: PropTypes.string,
  /**
   * Required.
   */
  required: PropTypes.bool,
  /**
   * Size.
   */
  size: PropTypes.oneOf(['large', 'medium', 'small']),
  /**
   * Success.
   */
  success: PropTypes.bool,
  /**
   * Text align.
   */
  textAlign: PropTypes.oneOf(['center', 'end', 'initial', 'start']),
  /**
   * Tooltip.
   */
  tooltip: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /**
   * Value.
   */
  value: PropTypes.instanceOf(Date).isRequired,
  /**
   * Vertical align.
   */
  verticalAlign: PropTypes.oneOf(['bottom', 'center', 'top']),
  /**
   * Width.
   */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * On change.
   */
  onChange: PropTypes.func.isRequired
}

export default DatePicker
